import React, { useEffect, useState } from 'react';
import { CommonAxiosGet } from '../../CommonUtils/CommonAxios';
import { config } from '../../CommonUtils/CommonURLS';

const STEP_META = {
    stu_profile: { label: "Student Profile", desc: "Profile setup complete" },
    stu_upload: { label: "Document Upload", desc: "All documents uploaded" },
    stu_payment: { label: "Payment", desc: "Fee payment verified" },
    from_coll_verify: { label: "From College Verify", desc: "Source college verified" },
    to_coll_verify: { label: "To College Verify", desc: "Destination college verified" },
    cte_approve: { label: "CHE Approval", desc: "CHE review approved" },
    super_approve: { label: "Super Approval", desc: "Awaiting Commissioner approval" },
};

const STATUS_ORDER = [
    "stu_profile", "stu_upload", "stu_payment",
    "from_coll_verify", "to_coll_verify", "cte_approve", "super_approve"
];

const STEP_FINAL_STATUS_KEY = {
    from_coll_verify: "from_college_status",
    to_coll_verify: "to_college_status",
    cte_approve: "ct_admin_status",
    super_approve: "final_status",
};

function toReadableStatus(statusValue) {
    if (!statusValue) return "Pending";
    return String(statusValue)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getStatusTheme(statusValue) {
    const normalized = String(statusValue || '').trim().toLowerCase();
    if (!normalized) {
        return { label: 'Pending', color: '#92400e', background: '#fef3c7' };
    }
    if (normalized.includes('reject')) {
        return { label: 'Rejected', color: '#b91c1c', background: '#fee2e2' };
    }
    if (normalized.includes('approve')) {
        return { label: 'Approved', color: '#166534', background: '#dcfce7' };
    }
    return { label: toReadableStatus(statusValue), color: '#1f2937', background: '#e5e7eb' };
}

function isRejectedStatus(statusValue) {
    return String(statusValue || '').trim().toLowerCase().includes('reject');
}

function isPendingStatus(statusValue) {
    return !String(statusValue || '').trim();
}

function getStageSummary(studata, steps) {
    const firstRejected = steps
        .map((stepKey) => ({
            stepKey,
            finalKey: STEP_FINAL_STATUS_KEY[stepKey],
            finalValue: STEP_FINAL_STATUS_KEY[stepKey] ? studata[STEP_FINAL_STATUS_KEY[stepKey]] : undefined,
        }))
        .find((item) => item.finalKey && isRejectedStatus(item.finalValue));

    if (firstRejected) {
        return { currentStep: firstRejected.stepKey };
    }

    const firstPendingDecision = steps
        .map((stepKey) => ({
            stepKey,
            workflowValue: studata[stepKey],
            finalKey: STEP_FINAL_STATUS_KEY[stepKey],
            finalValue: STEP_FINAL_STATUS_KEY[stepKey] ? studata[STEP_FINAL_STATUS_KEY[stepKey]] : undefined,
        }))
        .find((item) => item.finalKey && item.workflowValue === 'Done' && isPendingStatus(item.finalValue));

    if (firstPendingDecision) {
        return { currentStep: firstPendingDecision.stepKey };
    }

    const firstIncomplete = steps.find((stepKey) => studata[stepKey] !== 'Done');
    if (firstIncomplete) {
        return { currentStep: firstIncomplete };
    }

    return { currentStep: steps[steps.length - 1] };
}

function StatusCard({ label, desc, value, isLast, finalStatus, isCurrent }) {
    const done = value === "Done";
    const isWide = !done && isLast;
    const finalTheme = getStatusTheme(finalStatus);
    return (
        <div style={{
            background: '#fff',
            borderRadius: 12,
            border: isCurrent ? '2px solid #2563eb' : (done ? '1px solid #86efac' : '2px solid #fcd34d'),
            padding: '1rem',
            gridColumn: isWide ? 'span 2' : undefined,
            boxShadow: isCurrent ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : 'none',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{
                    width: 30, height: 30, borderRadius: '50%',
                    background: done ? '#dcfce7' : '#fef3c7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, color: done ? '#16a34a' : '#d97706'
                }}>
                    {done ? '✓' : '⏳'}
                </span>
                <span style={{
                    fontSize: 11, fontWeight: 500,
                    color: done ? '#15803d' : '#92400e',
                    background: done ? '#dcfce7' : '#fef3c7',
                    padding: '2px 8px', borderRadius: 6
                }}>
                    {done ? 'Step Done' : 'Step Pending'}
                </span>
                {isCurrent && (
                    <span style={{ fontSize: 11, color: '#1d4ed8', background: '#dbeafe', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                        Current
                    </span>
                )}
                {!done && !isCurrent && (
                    <span style={{
                        marginLeft: isCurrent ? 0 : 'auto',
                        flexBasis: isCurrent ? '100%' : 'auto',
                        textAlign: isCurrent ? 'right' : 'left',
                        fontSize: 11,
                        color: '#92400e',
                        background: '#fef3c7',
                        padding: '2px 8px',
                        borderRadius: 6,
                    }}>
                        Action required
                    </span>
                )}
            </div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111' }}>{label}</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6b7280' }}>{desc}</p>
            {(finalStatus !== undefined && finalStatus !== null) && (
                <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, color: '#01070f' }}>status:</span>
                    <span style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: finalTheme.color,
                        background: finalTheme.background,
                        padding: '2px 8px',
                        borderRadius: 999,
                    }}>
                        {finalTheme.label}
                    </span>
                </div>
            )}
        </div>
    );
}

export default function StudentStatusReport({ studentId }) {
    const [studata, setStuData] = useState(null);
    const [loading, setLoading] = useState(true);
    const studentID = studentId || sessionStorage.getItem('user_id');

    useEffect(() => {
        CommonAxiosGet(`${config.url.STU_URL}getStudentOverallStatusReport/${studentID}`).then((res) => {
            if (res?.data?.scode === '01' && res.data.data?.length) {
                setStuData(res.data.data[0]);
            }
            setLoading(false);
        });
    }, [studentID]);

    if (loading) return <p style={{ padding: '2rem', color: '#6b7280' }}>Loading...</p>;
    if (!studata) return <p style={{ padding: '2rem', color: '#ef4444' }}>No data found.</p>;

    // Dynamically pick only keys present in both payload and STEP_META

    const steps = STATUS_ORDER.filter(k => k in studata && STEP_META[k]);
    const doneCount = steps.filter(k => studata[k] === 'Done').length;
    const total = steps.length;
    const pct = Math.round((doneCount / total) * 100);
    const finalStatuses = Object.values(STEP_FINAL_STATUS_KEY)
        .map((statusKey) => studata[statusKey])
        .filter((statusValue) => statusValue !== undefined);
    const hasRejected = finalStatuses.some(isRejectedStatus);
    const allDone = doneCount === total;
    const stageSummary = getStageSummary(studata, steps);
    const initials = studata.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    const overallTheme = hasRejected
        ? { label: 'Rejected', color: '#b91c1c', background: '#fee2e2' }
        : allDone
            ? { label: 'Completed', color: '#15803d', background: '#dcfce7' }
            : { label: 'In Progress', color: '#92400e', background: '#fef3c7' };

    return (
        <div style={{ padding: '1.5rem', maxWidth: 720, margin: '0 auto', fontFamily: 'sans-serif' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 600, color: '#1d4ed8' }}>
                    {initials}
                </div>
                <div>
                    <p style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{studata.user_name}</p>
                    <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                        ID: {studata.user_id} &nbsp;·&nbsp; Aadhar: ****{studata.aadhar_no?.slice(-4)}
                    </p>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    <span style={{
                        fontSize: 14, padding: '5px 12px', borderRadius: 8, fontWeight: 600,
                        background: overallTheme.background,
                        color: overallTheme.color,
                    }}>
                        {overallTheme.label}
                    </span>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{ background: '#f3f4f6', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Overall Progress
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#22c55e', borderRadius: 4, transition: 'width 0.6s ease' }} />
                    </div>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{doneCount}/{total}</span>
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#6b7280' }}>
                    {doneCount} of {total} steps completed ({pct}%)
                </p>
            </div>

            {/* Step cards — fully driven by payload keys */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                {steps.map((key, idx) => (
                    <StatusCard
                        key={key}
                        label={STEP_META[key].label}
                        desc={STEP_META[key].desc}
                        value={studata[key]}
                        isLast={idx === steps.length - 1}
                        finalStatus={STEP_FINAL_STATUS_KEY[key] ? studata[STEP_FINAL_STATUS_KEY[key]] : undefined}
                        isCurrent={key === stageSummary.currentStep}
                    />
                ))}
            </div>
        </div>
    );
}